import {
  format,
  isThisMonth,
  isThisWeek,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import {
  ChecklistItem,
  ChecklistItemWithSubitems,
  ViewMode,
} from '@/apps/Checklist/stores/useChecklistStore';

const getGroupKey = (date: Date, viewMode: ViewMode): string => {
  switch (viewMode) {
    case 'daily':
      return format(date, 'yyyy-MM-dd');
    case 'weekly':
      return format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    case 'monthly':
      return format(startOfMonth(date), 'yyyy-MM');
    default:
      return format(date, 'yyyy-MM-dd');
  }
};

export const groupItemsByDate = (
  items: ChecklistItem[] | ChecklistItemWithSubitems[],
  viewMode: ViewMode = 'daily',
) => {
  const grouped = items.reduce(
    (acc, item) => {
      const key = getGroupKey(new Date(item.created_at), viewMode);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<string, (ChecklistItem | ChecklistItemWithSubitems)[]>,
  );

  return Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a));
};

const isInCurrentPeriod = (createdAt: string, viewMode: ViewMode): boolean => {
  const date = new Date(createdAt);
  switch (viewMode) {
    case 'daily':
      return isToday(date);
    case 'weekly':
      return isThisWeek(date, { weekStartsOn: 1 });
    case 'monthly':
      return isThisMonth(date);
    default:
      return isToday(date);
  }
};

export const calculateProgress = (
  items: ChecklistItem[],
  viewMode: ViewMode = 'daily',
) => {
  const periodItems = items.filter(
    (item) =>
      item.parent_id === null && isInCurrentPeriod(item.created_at, viewMode),
  );

  if (periodItems.length === 0) return 0;

  const completedItems = periodItems.filter((item) => item.completed);
  return Math.round((completedItems.length / periodItems.length) * 100);
};
